/**
 * T-48h Pre-Install Confirmation Engine
 * ─────────────────────────────────────────────────────────────────────────
 * For every job scheduled in the next 48 hours, derive a 5-point checklist
 * the dispatcher must clear before the truck rolls:
 *
 *   1. Customer confirmed       — did the homeowner reconfirm the appointment?
 *   2. Materials staged         — readiness in {Staged for Job, Loaded for Crew}?
 *   3. Permit verified          — permit Approved or Not Required?
 *   4. Balance secured          — deposit received (or no deposit required)?
 *   5. Access confirmed         — gate code / parking / pet notes captured?
 *
 * A job is "GO" when all 5 are clear, "AT RISK" when 1-2 fail, "HOLD" when 3+
 * fail. The dispatcher's morning is reviewing AT-RISK and HOLD — not status.
 *
 * Pure data: composes existing job state, no React.
 */
import { differenceInHours, parseISO, isValid } from "date-fns";
import type { Job } from "@/data/types";

export type ConfirmCheckKey = "customer" | "materials" | "permit" | "balance" | "access";

export interface ConfirmCheck {
  key: ConfirmCheckKey;
  label: string;
  passed: boolean;
  detail: string;
}

export type ConfirmStatus = "go" | "at_risk" | "hold";

export interface ConfirmEntry {
  job: Job;
  scheduledAtIso: string;
  hoursUntilInstall: number;
  status: ConfirmStatus;
  checks: ConfirmCheck[];
  failedCount: number;
  /** Operator one-liner — the action a dispatcher should take next. */
  nextAction: string;
}

interface BuildOpts {
  jobs: Job[];
  /** How far ahead to look. Default 48h. */
  hoursAhead?: number;
  referenceDate?: Date;
}

export function buildPreInstallQueue(opts: BuildOpts): ConfirmEntry[] {
  const { jobs, hoursAhead = 48, referenceDate = new Date() } = opts;

  const candidates = jobs.filter(j => {
    if (!j.scheduledInstallDate) return false;
    if (j.currentStage === "Completed" || j.currentStage === "In Progress") return false;
    const d = parseISO(j.scheduledInstallDate);
    if (!isValid(d)) return false;
    const hours = differenceInHours(d, referenceDate);
    return hours >= 0 && hours <= hoursAhead;
  });

  const entries: ConfirmEntry[] = candidates.map(j => {
    const d = parseISO(j.scheduledInstallDate);
    const hours = differenceInHours(d, referenceDate);

    const checks: ConfirmCheck[] = [];

    // 1. Customer confirmed — heuristic: activityHistory contains "confirmed"
    const customerConfirmed = (j.activityHistory || []).some(a =>
      /confirm/i.test(a.action || "")
    );
    checks.push({
      key: "customer",
      label: "Customer confirmed",
      passed: customerConfirmed,
      detail: customerConfirmed ? "Reconfirmed" : "No confirmation logged",
    });

    // 2. Materials staged
    const mr = j.materialReadiness;
    const materialsStaged = mr === "Staged for Job" || mr === "Loaded for Crew";
    checks.push({
      key: "materials",
      label: "Materials staged",
      passed: materialsStaged,
      detail: materialsStaged ? mr : `Currently ${mr || "Not Ordered"}`,
    });

    // 3. Permit verified
    const permitOk = !j.permitRequired || j.permitStatus === "Approved" || j.permitStatus === "Not Required";
    checks.push({
      key: "permit",
      label: "Permit verified",
      passed: permitOk,
      detail: !j.permitRequired ? "Not required" : j.permitStatus || "Unknown",
    });

    // 4. Balance secured — deposit invoice paid (or no deposit invoice on plan)
    const fin = j.financials;
    const depositRequired = (fin?.invoicePlan?.depositAmount ?? 0) > 0;
    const depositPaid = (fin?.invoices || [])
      .filter(inv => inv.type === "deposit" && inv.status === "paid")
      .reduce((s, inv) => s + inv.amount, 0);
    const balanceOk = !depositRequired || depositPaid > 0;
    checks.push({
      key: "balance",
      label: "Balance secured",
      passed: balanceOk,
      detail: !fin
        ? "No financial record"
        : depositRequired
          ? depositPaid > 0 ? "Deposit received" : "Deposit outstanding"
          : "No deposit required",
    });

    // 5. Access confirmed — heuristic: any access notes captured on the job
    const accessNote = (j.notes || "").length > 0;
    checks.push({
      key: "access",
      label: "Access confirmed",
      passed: accessNote,
      detail: accessNote ? "Notes on file" : "No access notes",
    });

    const failedCount = checks.filter(c => !c.passed).length;
    let status: ConfirmStatus;
    if (failedCount === 0) status = "go";
    else if (failedCount <= 2) status = "at_risk";
    else status = "hold";

    const firstFail = checks.find(c => !c.passed);
    const nextAction = status === "go"
      ? "Cleared — dispatch when ready"
      : `Resolve: ${firstFail?.label.toLowerCase()}`;

    return {
      job: j,
      scheduledAtIso: j.scheduledInstallDate,
      hoursUntilInstall: hours,
      status,
      checks,
      failedCount,
      nextAction,
    };
  });

  // Sort: holds first, then at-risk, then go. Within group: soonest first.
  const order: Record<ConfirmStatus, number> = { hold: 0, at_risk: 1, go: 2 };
  entries.sort((a, b) => {
    const so = order[a.status] - order[b.status];
    if (so !== 0) return so;
    return a.hoursUntilInstall - b.hoursUntilInstall;
  });

  return entries;
}
